import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const getContactName = (contact) => contact.name || contact.fullName || contact.label || 'Unnamed contact';

const contactTypeOptions = ['isp', 'store', 'vendor', 'support', 'other'];

const baseVisibilityOptions = ['standard', 'admin'];

const getVisibilityOptions = (isSuperAdmin) => (isSuperAdmin ? baseVisibilityOptions.concat('super_admin') : baseVisibilityOptions);

const getContactForm = (contact = {}) => ({
  contactType: contact.contactType || contact.type || 'other',
  visibility: contact.visibility || 'standard',
  label: contact.label || '',
  name: contact.name || contact.fullName || '',
  email: contact.email || '',
  phone: contact.phone || '',
  notes: contact.notes || ''
});

export default function LocationContactsPanel({
  contacts = [],
  isAdmin,
  isSuperAdmin,
  createContact,
  updateContact,
  deleteContact
}) {
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState(getContactForm());
  const [draftContacts, setDraftContacts] = useState({});
  const [actionError, setActionError] = useState('');
  const visibilityOptions = getVisibilityOptions(isSuperAdmin);

  useEffect(() => {
    setDraftContacts(Object.fromEntries(contacts.map(contact => [contact.id, getContactForm(contact)])));
  }, [contacts]);

  const updateNewContact = (event) => {
    const { name, value } = event.target;
    setNewContact(prev => Object.assign({}, prev, { [name]: value }));
  };

  const updateDraftContact = (contactId, event) => {
    const { name, value } = event.target;
    setDraftContacts(prev => Object.assign({}, prev, {
      [contactId]: Object.assign({}, prev[contactId], { [name]: value })
    }));
  };

  const handleCreateContact = async (event) => {
    event.preventDefault();
    if (!createContact) return;
    setActionError('');
    try {
      await createContact(newContact);
      setNewContact(getContactForm());
      setShowNewContact(false);
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleUpdateContact = async (contactId) => {
    if (!updateContact) return;
    setActionError('');
    try {
      await updateContact(contactId, draftContacts[contactId]);
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!deleteContact) return;
    setActionError('');
    try {
      await deleteContact(contactId);
    } catch (error) {
      setActionError(error.message);
    }
  };

  return (
    <section id="contacts" className="rounded-lg border border-white/10 bg-[#0b0b0b] p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Contacts</h2>
          <p className="text-xs text-gray-500">Records returned for this location</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              type="button"
              aria-label="Add contact"
              onClick={() => setShowNewContact(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-gray-300 transition-colors hover:bg-white/[0.08] hover:text-white"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <span className="rounded-full border border-white/10 px-2 py-1 text-xs font-semibold text-gray-400">
            {contacts.length} shown
          </span>
        </div>
      </div>

      {actionError && (
        <div className="mb-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-100">
          {actionError}
        </div>
      )}

      {isAdmin && showNewContact && (
        <form onSubmit={handleCreateContact} className="mb-4 grid gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 sm:grid-cols-2">
          <ContactFields
            values={newContact}
            visibilityOptions={visibilityOptions}
            onChange={updateNewContact}
          />
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <button type="submit" className="rounded-lg bg-white px-3 py-2 text-xs font-semibold text-black transition-colors hover:bg-gray-200">
              Save Contact
            </button>
            <button
              type="button"
              onClick={() => {
                setNewContact(getContactForm());
                setShowNewContact(false);
              }}
              className="rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-gray-300 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {contacts.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {contacts.map(contact => (
            <article key={contact.id || contact.email || contact.phone || getContactName(contact)} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-white">{getContactName(contact)}</h3>
                  {contact.role && <p className="mt-1 text-xs text-gray-500">{contact.role}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {(isAdmin || isSuperAdmin) && contact.visibility && (
                    <span className="rounded-full border border-white/10 px-2 py-1 text-[11px] font-semibold text-gray-400">
                      {contact.visibility}
                    </span>
                  )}
                  {isAdmin && contact.id && (
                    <button
                      type="button"
                      aria-label="Delete contact"
                      onClick={() => handleDeleteContact(contact.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-gray-400 transition-colors hover:bg-red-500/10 hover:text-red-100"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-gray-300">
                {contact.email && <p>{contact.email}</p>}
                {contact.phone && <p>{contact.phone}</p>}
                {contact.notes && <p className="text-gray-400">{contact.notes}</p>}
              </div>
              {isAdmin && contact.id && (
                <div className="mt-3 grid gap-3 border-t border-white/10 pt-3 sm:grid-cols-2">
                  <ContactFields
                    values={draftContacts[contact.id] || getContactForm(contact)}
                    visibilityOptions={visibilityOptions}
                    onChange={(event) => updateDraftContact(contact.id, event)}
                  />
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={() => handleUpdateContact(contact.id)}
                      className="rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-gray-300 transition-colors hover:bg-white/[0.06] hover:text-white"
                    >
                      Save Contact
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm text-gray-500">No contacts returned</div>
      )}
    </section>
  );
}

function ContactFields({ values, visibilityOptions, onChange }) {
  return (
    <>
      <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
        Type
        <select name="contactType" value={values.contactType} onChange={onChange} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30">
          {contactTypeOptions.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
      </label>
      <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
        Visibility
        <select name="visibility" value={values.visibility} onChange={onChange} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30">
          {visibilityOptions.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
      </label>
      <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
        Label
        <input name="label" value={values.label} onChange={onChange} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30" />
      </label>
      <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
        Name
        <input name="name" value={values.name} onChange={onChange} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30" />
      </label>
      <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
        Email
        <input name="email" value={values.email} onChange={onChange} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30" />
      </label>
      <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
        Phone
        <input name="phone" value={values.phone} onChange={onChange} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30" />
      </label>
      <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500 sm:col-span-2">
        Notes
        <textarea name="notes" value={values.notes} onChange={onChange} rows={2} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30" />
      </label>
    </>
  );
}
